import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useTheme } from "../../contexts/ThemeContext";

export const SettingsModal = ({ open, handleClose, workflow, onSave }) => {
  const { darkMode } = useTheme();

  const [timezone, setTimezone] = useState(workflow?.timezone || "Asia/Kolkata");
  const [reEnrollPolicy, setReEnrollPolicy] = useState(workflow?.reEnrollPolicy || "skip");
  const [maxActivePerContact, setMaxActivePerContact] = useState(workflow?.maxActivePerContact ?? 1);
  const [respectQuietHours, setRespectQuietHours] = useState(workflow?.respectQuietHours ?? true);
  const [quietHoursStart, setQuietHoursStart] = useState(workflow?.quietHoursStart || "20:00");
  const [quietHoursEnd, setQuietHoursEnd] = useState(workflow?.quietHoursEnd || "09:00");
  const [description, setDescription] = useState(workflow?.description || "");
  const [campaignName, setCampaignName] = useState(workflow?.campaignName || "");

  useEffect(() => {
    if (workflow) {
      setTimezone(workflow.timezone || "Asia/Kolkata");
      setReEnrollPolicy(workflow.reEnrollPolicy || "skip");
      setMaxActivePerContact(workflow.maxActivePerContact ?? 1);
      setRespectQuietHours(workflow.respectQuietHours ?? true);
      setQuietHoursStart(workflow.quietHoursStart || "20:00");
      setQuietHoursEnd(workflow.quietHoursEnd || "09:00");
      setDescription(workflow.description || "");
      setCampaignName(workflow.campaignName || "");
    }
  }, [workflow]);

  const handleSave = () => {
    onSave({
      description,
      // Blank is sent as null so the lead falls back to the default campaign
      // rather than being attributed to an empty string.
      campaignName: campaignName.trim() || null,
      timezone,
      reEnrollPolicy,
      maxActivePerContact: Number(maxActivePerContact) || 1,
      respectQuietHours,
      quietHoursStart: respectQuietHours ? quietHoursStart : null,
      quietHoursEnd: respectQuietHours ? quietHoursEnd : null,
    });
    handleClose();
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500">
            <TuneIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              Journey Settings & Guardrails
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Configure compliance, quiet hours, and duplicate enrollment policies.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <TextField
            fullWidth
            size="small"
            label="Journey Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="E.g. Inbound call follow-up and SMS confirmation..."
          />

          <TextField
            fullWidth
            size="small"
            label="Campaign Name"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="E.g. Reel-20-80-Payment"
            helperText="LeadSquared campaign for leads this journey brings in. Leave blank for Instagram-New."
          />

          <FormControl fullWidth size="small">
            <InputLabel id="tz-label">Timezone</InputLabel>
            <Select
              labelId="tz-label"
              value={timezone}
              label="Timezone"
              onChange={(e) => setTimezone(e.target.value)}
            >
              <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST +5:30)</MenuItem>
              <MenuItem value="UTC">UTC (GMT +0:00)</MenuItem>
              <MenuItem value="America/New_York">America/New_York (EST/EDT)</MenuItem>
              <MenuItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</MenuItem>
              <MenuItem value="Europe/London">Europe/London (GMT/BST)</MenuItem>
              <MenuItem value="Asia/Dubai">Asia/Dubai (GST +4:00)</MenuItem>
            </Select>
          </FormControl>

          <div className="grid grid-cols-2 gap-3">
            <FormControl size="small">
              <InputLabel id="reenroll-label">Re-enrollment Policy</InputLabel>
              <Select
                labelId="reenroll-label"
                value={reEnrollPolicy}
                label="Re-enrollment Policy"
                onChange={(e) => setReEnrollPolicy(e.target.value)}
              >
                <MenuItem value="skip">Skip (Ignore duplicates)</MenuItem>
                <MenuItem value="replace">Replace Existing Run</MenuItem>
                <MenuItem value="allow_parallel">Allow Parallel Runs</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="number"
              label="Max Active Per Contact"
              value={maxActivePerContact}
              onChange={(e) => setMaxActivePerContact(Math.max(1, Number(e.target.value)))}
              inputProps={{ min: 1, max: 10 }}
            />
          </div>

          <div className={`p-3.5 rounded-xl border ${darkMode ? "bg-zinc-900/60 border-zinc-700" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-xs font-semibold ${darkMode ? "text-white" : "text-slate-800"}`}>
                  Respect Quiet Hours Window
                </div>
                <div className={`text-[11px] ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Outbound calls and SMS will hold and resume during acceptable daytime hours.
                </div>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={respectQuietHours}
                    onChange={(e) => setRespectQuietHours(e.target.checked)}
                    color="primary"
                  />
                }
                label=""
              />
            </div>

            {respectQuietHours && (
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-200 dark:border-zinc-700">
                <TextField
                  size="small"
                  label="Quiet Hours Start (Evening)"
                  value={quietHoursStart}
                  onChange={(e) => setQuietHoursStart(e.target.value)}
                  placeholder="20:00"
                  helperText="E.g. 20:00 (8:00 PM)"
                />
                <TextField
                  size="small"
                  label="Quiet Hours End (Morning)"
                  value={quietHoursEnd}
                  onChange={(e) => setQuietHoursEnd(e.target.value)}
                  placeholder="09:00"
                  helperText="E.g. 09:00 (9:00 AM)"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            style={{ backgroundColor: "#4f46e5" }}
          >
            Apply Settings
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

